// Per-game detail page — first surface that fuses the live WS feed (price
// line) with hydra analytics (trade bubbles + wallet table). Deliberate
// lane carve-out: outside G3 but allowed to call hydraAnalytics. See
// CLAUDE.md "Live feed (g1) vs analytics (G3)".
//
// Three modes (planned): 'pre' (before event start), 'live', 'archived'. As
// of Jul-22 only 'live' is reachable — the Archived/Analytics tab is
// disabled in the UI (see ViewToggle) because that surface never got past
// stop-gap. Pre is likewise stubbed until we have a story for pre-game data.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { composeGameKeyV2, composeHydraGameKey, gameKeyMatchesUrl } from '@/services/analyticsGameKey';
import { type TradeRow } from '@/services/hydraAnalytics';
import { useMarketStore } from '@/stores/marketStore';
import { MULTI_OUTCOME_SPORTS, SPORT_TABS } from '@/views/ValueDashboard/types';
import { classifyPolyExtendedTicker } from '@/lib/kalshiTicker/polyPropsEvents';
import type {
  GameEventRow,
  GameRow,
  MultiOutcomeEvent,
  SeriesOutcome,
  Sport,
} from '@/views/ValueDashboard/types';
import {
  buildMultiOutcomeBetContext,
  multiOutcomeDrawRow,
  multiOutcomePairRow,
  multiOutcomeRoles,
  synthesizeFromMultiOutcome,
} from '@/views/ValueDashboard/multiOutcome';
import type { BetLineContext } from '@/views/ValueDashboard/GameDetailPanel';
import { MiniLineStrip } from '@/views/SpreadsTotals/MiniLineStrip';
import { PriceChart, type ChartMode } from './PriceChart';
import { TradesTable } from './TradesTable';
import { useGameTrades, type GameTradesMode } from './useGameTrades';
import { lineInfoFromTicker, tickerNamedTeam, type Venue } from './helpers';
import { ConsensusChart } from './ConsensusChart';
import { GameScoreHeader } from './GameScoreHeader';
import { MoneylineSection } from './MoneylineSection';
import { SpreadTotalSection, type ActiveSpreadTotal } from './SpreadTotalSection';
import { GameLinesSection } from './GameLinesSection';
import { resolveLiveCfbEventPanelGame } from './cfbEventRows';
import { useOddsStream } from './useOddsStream';
import { isFullGameResult } from '@/services/oddsStreamHub';
import { GameDetailPanel, type TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { PositionSidebar, PositionsWidget } from '../ValueDashboard/PositionSidebar';
import { OrderbooksPausedBadge } from '@/components/atoms/OrderbooksPausedBadge';
import { OneClickArmControl } from '@/views/ValueDashboard/OneClickArmControl';
import { AttackArmControl } from '@/views/ValueDashboard/AttackArmControl';
import { Lt2HotkeyListener } from '@/components/Lt2HotkeyListener';
import { Lt2TargetReadout } from '@/components/Lt2TargetReadout';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useOneClickStore } from '@/stores/oneClickStore';
import { tryOneClickFromMoneylineCtx } from '@/views/ValueDashboard/oneClickIntercept';
import { LT2_GD_ML_CARD_SCOPE } from '@/views/ValueDashboard/lt2Moneyline';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useAuthStore } from '@/stores/authStore';
import {
  linePriceCents,
  spreadLineKey,
  resolveLineIndexByKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
} from '../SpreadsTotals/synthesize';
import type { TeamSide, TradeTicketContext } from '../ValueDashboard/types';

type MarketTypeFilter = 'moneyline' | 'spread' | 'total';

const MARKET_TYPES: ReadonlyArray<{
  id: MarketTypeFilter;
  label: string;
}> = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread', label: 'Spread' },
  { id: 'total', label: 'Total' },
];

/** Lossless map from the GameDetail page's tradeCtx (TradeTicketContext)
 *  to GameDetailPanel's TradeIntent prop shape. teamSide → side; the
 *  prefill / postOnly / orderSide carry through unchanged. */
function tradeCtxToTradeIntent(ctx: TradeTicketContext): TradeIntent {
  return {
    side: ctx.teamSide,
    venue: ctx.venue,
    prefillPrice: ctx.prefillPrice,
    initialOrderSide: ctx.initialOrderSide,
    postOnly: ctx.postOnly,
  };
}

// The per-game-detail URL identity. sport/gameNo are absent on the legacy
// sport-less route (sport = wildcard match, gameNo defaults to 1); a sport-aware
// route with a `:gameNo` slot addresses a specific doubleheader game.
interface GameUrlParts {
  sport?: string;
  date?: string;
  teamA?: string;
  teamB?: string;
  gameNo?: string;
}

// Scan every sport's getGames() output for a GameRow whose doubleheader-safe
// v2 key matches the URL. Cheap — these maps are small. Returns null for
// archived games whose live frames have been pruned (expected: hydra's
// price_ticks have 7d retention, so a 30-day-old game won't have a live row to
// mount; the chart falls back to bubbles-only).
function findGameRow(url: GameUrlParts): GameRow | null {
  const store = useMarketStore.getState();
  for (const tab of SPORT_TABS) {
    const games = store.getGames(tab.id as Sport, 'moneyline', {
      dateFilter: 'all',
      statusFilter: 'all',
    });
    const hit = games.find((g) => gameKeyMatchesUrl(g.key, url));
    if (hit) return hit;
  }
  return null;
}

// Multi-outcome (soccer 3-way) resolution — getGames early-returns for
// MULTI_OUTCOME_SPORTS, so those games come from getMultiOutcomeEvents
// instead, with the outcomes pre-resolved to their away/home/draw roles.
interface MultiOutcomeGame {
  event: MultiOutcomeEvent;
  away?: SeriesOutcome;
  home?: SeriesOutcome;
  draw?: SeriesOutcome;
}

function findMultiOutcomeGame(url: GameUrlParts): MultiOutcomeGame | null {
  const store = useMarketStore.getState();
  for (const sport of MULTI_OUTCOME_SPORTS) {
    const event = store
      .getMultiOutcomeEvents(sport)
      .find((e) => gameKeyMatchesUrl(e.key, url));
    if (event) return { event, ...multiOutcomeRoles(event) };
  }
  return null;
}

// Parallel to findGameRow — walks every sport for a GameEventRow matching
// the canonical key. GameEventRow carries the spread/total line arrays
// the line selector needs. Returns null when no event has spread/total
// markets for this game yet (e.g. pre-game markets not posted, or
// hydra hasn't ingested them).
function findGameEvent(url: GameUrlParts): GameEventRow | null {
  const store = useMarketStore.getState();
  for (const tab of SPORT_TABS) {
    const events = store.getGameEvents(tab.id as Sport, {
      dateFilter: 'all',
      statusFilter: 'all',
    });
    const hit = events.find((e) => gameKeyMatchesUrl(e.key, url));
    if (hit) return hit;
  }
  return null;
}

/** Re-synthesize a live GameRow for a spread/total tradeCtx so its
 *  K + P prices tick with marketStore frames instead of frozen at
 *  click time. Parses the kind ("spread" | "total") + Kalshi ticker
 *  out of the synth key, refinds the live event + line, and rebuilds.
 *
 *  synthKey shape (see synthesizeFromSpread / synthesizeFromTotal):
 *    `<eventKey>|spread|<kalshiTicker>`  or
 *    `<eventKey>|total|<kalshiTicker>`
 *
 *  Returns null when the key doesn't match either pattern, when the
 *  live event isn't ingested, or when the line ticker isn't present
 *  on the current event (e.g. line dropped from book). Caller falls
 *  back to the stale snapshot in that case so the panel doesn't blank. */
function findLiveSynthGameRow(synthKey: string, url: GameUrlParts): GameRow | null {
  const parts = synthKey.split('|');
  if (parts.length < 4) return null;
  const ticker = parts[parts.length - 1];
  const kind = parts[parts.length - 2];
  if (kind !== 'spread' && kind !== 'total') return null;
  const event = findGameEvent(url);
  if (!event) return null;
  if (kind === 'spread') {
    const line = event.spreads.find(
      (s) => s.kalshi?.marketTicker === ticker || s.polymarket?.marketTicker === ticker,
    );
    return line ? synthesizeFromSpread(event, line) : null;
  }
  const line = event.totals.find(
    (t) => t.kalshi?.marketTicker === ticker || t.polymarket?.marketTicker === ticker,
  );
  return line ? synthesizeFromTotal(event, line) : null;
}

// Closest-to-50¢ default — same heuristic SpreadsTotals uses for the
// inline strip: picks the line whose Kalshi YES price is nearest 50¢,
// which is the consensus line bettors look at first. Falls back to
// index 0 when no line has a parseable YES price.
function formatSignedLine(signed: number): string {
  const sign = signed < 0 ? '-' : '+';
  return `${sign}${Math.abs(signed)}`;
}

// Canonical chip shape used by both the gameEvent-sourced path and the
// trade-derived fallback below. `tickers` lets the consumer filter
// chart/trades to a specific line without re-deriving from the chip key.
interface LineChip {
  key: string;
  label: string;
  priceCents: number | null;
  tickers: { kalshi: string | null; polymarket: string | null };
}

// Fallback line chips derived from the trades themselves. Used when
// marketStore's GameEventRow doesn't carry spreads/totals for this
// game (commonly because hydra hasn't tagged canonicalGameKey on
// KX*SPREAD/KX*TOTAL WS frames — see
// [[project_hydra_ask_canonical_key_on_spread_total]] — or because
// the game's live markets are closed and we only have historical
// analytics rows).
//
// Venue-merges Kalshi and Polymarket trades for the same line into a
// single chip, so a 6.5 total with both Kalshi and Poly markets shows
// once (not twice). Grouping key:
//   • Totals: magnitude alone (no team/side).
//   • Spreads: magnitude + favoredSide (away|home) — same key both
//     venues' parsers populate, so a Kalshi "BOS covers -1.5" merges
//     with the matching Poly "spread-home-1pt5" when BOS is home.
//
// Sort: parsed line value ascending; chips without a parseable line
// go to the end. Default selection: priceCents closest to 50¢.
function deriveLineChipsFromTrades(
  trades: TradeRow[],
  store: ReturnType<typeof useMarketStore.getState>,
): LineChip[] {
  // First pass: collect last-yes-price + venue per unique ticker.
  type TickerAcc = {
    lastYesCents: number | null;
    lastTsMs: number;
    venue: Venue;
  };
  const byTicker = new Map<string, TickerAcc>();
  for (const r of trades) {
    const venue: Venue =
      (r.source ?? '').toLowerCase() === 'polymarket' ? 'polymarket' : 'kalshi';
    const ts = Date.parse(r.block_ts ?? r.ts);
    if (!Number.isFinite(ts)) continue;
    const side = (r.side ?? '').toLowerCase();
    const yesCents =
      side === 'yes' ? r.price : side === 'no' ? 100 - r.price : null;
    const existing = byTicker.get(r.ticker);
    if (!existing || ts > existing.lastTsMs) {
      byTicker.set(r.ticker, { lastYesCents: yesCents, lastTsMs: ts, venue });
    }
  }

  // Second pass: bucket by line value (and favoredSide for spreads)
  // so cross-venue tickers for the same line collapse into one chip.
  interface Group {
    sortValue: number;
    label: string | null;          // resolved when first ticker arrives
    favoredTeam: string | null;    // promoted from Kalshi when available
    priceCents: number | null;     // closest-to-50 across both venues
    kalshiTicker: string | null;
    polyTicker: string | null;
  }
  const groups = new Map<string, Group>();
  const closerTo50 = (a: number | null, b: number | null): number | null => {
    if (a == null) return b;
    if (b == null) return a;
    return Math.abs(a - 50) < Math.abs(b - 50) ? a : b;
  };

  for (const [ticker, info] of byTicker) {
    // Prefer the live HydraMarket subtitle for the label — canonical
    // across sports. Falls back to ticker-string parsing for archived
    // games where marketStore doesn't have the live frame.
    const subtitle = store.getMarket(ticker)?.subtitle ?? '';
    const subtitleMatch = subtitle.match(/([+-]?\d+(?:\.\d+)?)/);
    const lineInfo = lineInfoFromTicker(ticker);

    let groupKey: string;
    let sortValue: number;
    let candidateLabel: string;
    let favoredTeam: string | null = null;

    if (lineInfo) {
      groupKey =
        lineInfo.marketType === 'spread'
          ? `s|${lineInfo.magnitude}|${lineInfo.favoredSide ?? 'unk'}`
          : `t|${lineInfo.magnitude}`;
      sortValue = lineInfo.magnitude;
      favoredTeam = lineInfo.favoredTeam;
      candidateLabel = lineInfo.favoredTeam
        ? `${lineInfo.favoredTeam} ${lineInfo.magnitude}`
        : String(lineInfo.magnitude);
    } else if (subtitleMatch) {
      // No structured parse — fall back to subtitle's first number.
      // Each such ticker becomes its own group (can't safely merge).
      const value = Number(subtitleMatch[1]);
      groupKey = `x|${ticker}`;
      sortValue = Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
      candidateLabel = subtitleMatch[1];
    } else {
      // Last resort — group key is the ticker itself, label truncated.
      groupKey = `x|${ticker}`;
      sortValue = Number.POSITIVE_INFINITY;
      candidateLabel =
        ticker.length > 14
          ? `${ticker.slice(0, 8)}…${ticker.slice(-4)}`
          : ticker;
    }

    let g = groups.get(groupKey);
    if (!g) {
      g = {
        sortValue,
        label: null,
        favoredTeam: null,
        priceCents: null,
        kalshiTicker: null,
        polyTicker: null,
      };
      groups.set(groupKey, g);
    }
    if (info.venue === 'kalshi') {
      // First kalshi wins (groups should have at most one per venue).
      g.kalshiTicker ??= ticker;
      // Kalshi carries the team-code label; promote when available so
      // a Poly-first chip gets its team prefix once Kalshi joins.
      if (favoredTeam) g.favoredTeam = favoredTeam;
    } else {
      g.polyTicker ??= ticker;
    }
    // Prefer Kalshi-derived label (carries team), fall back to whatever
    // the first ticker produced.
    if (!g.label || (favoredTeam && !g.label.includes(' '))) {
      g.label = candidateLabel;
    }
    g.priceCents = closerTo50(g.priceCents, info.lastYesCents);
  }

  // Emit chips, sorted by parsed line value ascending.
  const chips: Array<LineChip & { sortValue: number }> = [];
  for (const g of groups.values()) {
    const labelWithTeam =
      g.favoredTeam && g.label && !g.label.startsWith(g.favoredTeam)
        ? `${g.favoredTeam} ${g.label}`
        : (g.label ?? '?');
    chips.push({
      key: g.kalshiTicker ?? g.polyTicker ?? labelWithTeam,
      label: labelWithTeam,
      priceCents: g.priceCents,
      tickers: { kalshi: g.kalshiTicker, polymarket: g.polyTicker },
      sortValue: g.sortValue,
    });
  }
  chips.sort((a, b) => a.sortValue - b.sortValue);
  return chips.map(({ sortValue: _ignored, ...c }) => c);
}

// Closest-to-50¢ default — same heuristic SpreadsTotals uses for the
// inline strip. Falls back to index 0 when no chip has a YES price.
function pickDefaultChipIdx(chips: readonly LineChip[]): number {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < chips.length; i++) {
    const p = chips[i].priceCents;
    if (p == null) continue;
    const d = Math.abs(p - 50);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function GameDetail() {
  const { sport, date, teamA, teamB, gameNo } = useParams<{
    sport?: string;
    date: string;
    teamA: string;
    teamB: string;
    gameNo?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const version = useMarketStore((s) => s.version);

  // URL identity used to find the matching live row(s). sport/gameNo absent on
  // the legacy route (sport wildcard, gameNo 1); the sport-aware route with a
  // `:gameNo` slot pins a specific doubleheader game.
  const urlParts = useMemo<GameUrlParts>(
    () => ({ sport, date, teamA, teamB, gameNo }),
    [sport, date, teamA, teamB, gameNo],
  );

  // 3-seg analytics key (date|TEAM_A|TEAM_B) for the trades/history surface,
  // which is gameNo-blind. Doubles as the URL-validity gate.
  const canonicalKey = useMemo(
    () => composeHydraGameKey({ date, teamA, teamB }),
    [date, teamA, teamB],
  );

  // Soccer (3-way) games resolve through the multi-outcome path; binary
  // sports through getGames. `game` is the binary row, or for soccer the
  // away/home pair projected into the same binary shape (multiOutcomePairRow)
  // so the header / score / sections render unchanged. The Draw renders as
  // an extra MoneylineSection row, and trade clicks re-map to per-outcome
  // synths below so ticket labels read "X does not win", not "(opp wins)".
  const multiGame = useMemo(() => {
    if (!canonicalKey) return null;
    return findMultiOutcomeGame(urlParts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParts, canonicalKey, version]);

  const game = useMemo(() => {
    if (!canonicalKey) return null;
    const binary = findGameRow(urlParts);
    if (binary) return binary;
    return multiGame?.away && multiGame.home
      ? multiOutcomePairRow(multiGame.event, multiGame.away, multiGame.home)
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParts, canonicalKey, version, multiGame]);

  // Per-game key for the odds stream — derived purely from the URL so it's stable
  // across the async row load (using the matched row's key would re-fire the reset
  // effects below once the store populates, wiping trade context). Sport-aware URLs
  // compose the doubleheader-safe v2 key (addresses a specific game); legacy URLs
  // fall back to the 3-seg analytics key (an undisambiguated key resolves to the
  // live game). Also doubles as the pure-from-URL game identity for resets — it
  // carries gameNo via composeGameKeyV2, so it changes on navigation between a
  // doubleheader's two games. SourcesPage computes this identically, so both pages
  // share one EventSource per game.
  const oddsKey = useMemo(
    () => composeGameKeyV2({ sport, date, teamA, teamB, gameNo }) ?? canonicalKey,
    [sport, date, teamA, teamB, gameNo, canonicalKey],
  );

  const drawGame = useMemo(
    () => (multiGame?.draw ? multiOutcomeDrawRow(multiGame.event, multiGame.draw) : null),
    [multiGame],
  );

  // Soccer knockout "To Advance" 2-way (who advances incl. ET + pens). When it
  // exists it becomes the HEADLINE Moneyline (a plain binary, no draw) and the
  // regulation 1X2 demotes into Game Lines under "90 min". It shares the 1X2's
  // canonicalGameKey, so resolve it off the already-matched multi-outcome event
  // key. Null for group-stage games (no advance market) → headline stays the 1X2.
  const advanceGame = useMemo(() => {
    if (!multiGame) return null;
    return useMarketStore.getState().getAdvanceGame(multiGame.event.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiGame, version]);

  // What the top Moneyline section renders: the advance binary when present
  // (no Draw row), else the regulation 1X2 (3-way with the Draw) exactly as
  // before. `game` itself stays the 1X2 pair for the header / Game Lines / score.
  const moneylineGame = advanceGame ?? game;
  const moneylineDrawGame = advanceGame ? null : drawGame;

  // Back button: a plain navigate(-1). The dashboard's sport filter lives in
  // its URL (?sport=…), so history restores it natively — no sport handoff
  // needed (the sessionStorage returnSport stash this used to write is gone).
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Two teams come straight from the URL (always alpha-sorted because of
  // composeHydraGameKey upstream). We use the URL casing for the selector
  // even when the GameRow isn't ingested, so archived games still get a
  // useful toolbar.
  const bothTeams = useMemo<readonly [string, string]>(() => {
    const a = (teamA ?? '').toUpperCase();
    const b = (teamB ?? '').toUpperCase();
    return [a, b];
  }, [teamA, teamB]);

  // ── View mode + tab state ──────────────────────────────────────
  // Live is the only working view, so every game opens on it — no URL-date
  // heuristic, no Unabated statusId auto-flip into archived. The Analytics
  // tab is greyed out in ViewToggle until that surface is real; the
  // `viewMode === 'archived'` branches below are left intact so re-enabling
  // is a matter of un-disabling the button.
  const [viewMode, setViewMode] = useState<'live' | 'archived'>('live');
  const chartMode: ChartMode = 'archived';
  const [marketType, setMarketType] = useState<MarketTypeFilter>('moneyline');
  const lt2Access = useLt2Access();

  // GameEventRow gates the line selector for spread/total. Recomputed
  // on marketStore version bumps so newly-ingested lines appear without
  // re-mount.
  const gameEvent = useMemo(() => {
    if (!canonicalKey) return null;
    return findGameEvent(urlParts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParts, canonicalKey, version]);

  // Selected line per market type. LT2 keeps both the legacy index and the
  // stable market key so a live feed reorder cannot silently retarget the
  // order surface; flag-off behavior remains the legacy index contract.
  // State persists across toggle flips so flipping Spread → Total → Spread
  // brings the same spread back. The default lands on the closest-to-50¢ line the first time
  // we see lines arrive — see the seeding effect below useGameTrades
  // (it needs the trades array as a fallback chip source when hydra
  // doesn't expose spreads/totals via GameEventRow).
  const [selectedSpreadIdx, setSelectedSpreadIdx] = useState<number>(0);
  const [selectedTotalIdx, setSelectedTotalIdx] = useState<number>(0);
  const [selectedSpreadKey, setSelectedSpreadKey] = useState<string | null>(null);
  const [selectedTotalKey, setSelectedTotalKey] = useState<string | null>(null);
  const [defaultsSeeded, setDefaultsSeeded] = useState<{
    spread: boolean;
    total: boolean;
  }>({ spread: false, total: false });
  // userPickedTeam is null until the user clicks a team chip. While null,
  // selectedTeam is derived (auto-defaults to the favored team). After
  // they click, their pick wins until the URL changes. Seed from the
  // optional `?team=XYZ` query string so the dashboard can deep-link to
  // a specific team's perspective (e.g. "click NYR on the archived row").
  const initialUrlTeam = useMemo(() => {
    const t = (searchParams.get('team') ?? '').toUpperCase();
    if (!t) return null;
    if (t === (teamA ?? '').toUpperCase() || t === (teamB ?? '').toUpperCase()) {
      return t;
    }
    return null;
  }, [searchParams, teamA, teamB]);

  // `?ticker=…&side=…` deep-link from the Positions page so a click on
  // a held position / open order lands here with the right line
  // pre-expanded and the trade ticket pre-filled. Parsed once on mount;
  // navigation back to the same page with different params re-derives
  // through the standard searchParams change.
  const focusTicker = searchParams.get('ticker');
  const focusSide: 'yes' | 'no' =
    (searchParams.get('side') ?? '').toLowerCase() === 'yes' ? 'yes' : 'no';
  const [userPickedTeam, setUserPickedTeam] = useState<string | null>(initialUrlTeam);
  const [enabledVenues, setEnabledVenues] = useState<ReadonlySet<Venue>>(
    () => new Set<Venue>(['kalshi', 'polymarket']),
  );

  // Data load extent: 'event' (default) only fetches trades from the
  // Kalshi-derived event start onward; 'all' loads the full pre-game →
  // close history. Drives both the chart bubbles and the trades table.
  // Toggled via the EVENT/ALL buttons on TradesTable.
  const [dataMode, setDataMode] = useState<GameTradesMode>('event');

  // Poll the trades endpoint every 5s while the game is still trading.
  // Once the game is over (gameState 'final'), or the game isn't in
  // marketStore at all (archived), polling stops — no reason to keep
  // hitting hydra for trades that aren't changing.
  const pollEnabled = game != null && game.gameState !== 'final';

  // Skip the analytics fetch entirely on the Live view — that surface is
  // SSE-only (Unabated consensus) and any /api/analytics/trades hit is
  // wasted bytes. The Archived view still pulls trades the moment the
  // user flips the toggle.
  const { status, items: trades, loaded, total, error } = useGameTrades(
    viewMode === 'archived' ? canonicalKey : null,
    marketType,
    dataMode,
    pollEnabled && viewMode === 'archived',
  );

  // Line chips for the spread/total scroller. Primary source is
  // marketStore's GameEventRow (works when hydra tags canonicalGameKey
  // on the spread/total WS frames and live markets are open). Fallback
  // derives chips from the trades themselves — covers archived games
  // past hydra's live window and the per-memory canonical-key gap.
  const lineChips: LineChip[] = useMemo(() => {
    if (gameEvent) {
      if (marketType === 'spread' && gameEvent.spreads.length > 0) {
        return gameEvent.spreads.map((sp) => {
          const favoredCode =
            sp.favored === 'away' ? gameEvent.awayCode : gameEvent.homeCode;
          return {
            key: spreadLineKey(sp),
            label: `${favoredCode} ${formatSignedLine(sp.line)}`,
            priceCents: linePriceCents(sp),
            tickers: {
              kalshi: sp.kalshi?.marketTicker ?? null,
              polymarket: sp.polymarket?.marketTicker ?? null,
            },
          };
        });
      }
      if (marketType === 'total' && gameEvent.totals.length > 0) {
        return gameEvent.totals.map((t) => ({
          key: totalLineKey(t),
          label: String(t.line),
          priceCents: linePriceCents(t),
          tickers: {
            kalshi: t.kalshi?.marketTicker ?? null,
            polymarket: t.polymarket?.marketTicker ?? null,
          },
        }));
      }
    }
    if (marketType !== 'spread' && marketType !== 'total') return [];
    if (trades.length === 0) return [];
    return deriveLineChipsFromTrades(trades, useMarketStore.getState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameEvent, marketType, trades, version]);

  // Default-select closest-to-50¢ chip the first time chips arrive for
  // each market type. defaultsSeeded prevents re-seeding after the
  // user picks a different line. Reset deliberately not wired — if
  // the chip list changes shape mid-session the user keeps their
  // existing index (clamped at render time when out of bounds).
  useEffect(() => {
    if (marketType !== 'spread' && marketType !== 'total') return;
    if (lineChips.length === 0) return;
    const seeded =
      marketType === 'spread' ? defaultsSeeded.spread : defaultsSeeded.total;
    if (seeded) return;
    const idx = pickDefaultChipIdx(lineChips);
    if (marketType === 'spread') {
      setSelectedSpreadIdx(idx);
      setSelectedSpreadKey(lineChips[idx]?.key ?? null);
      setDefaultsSeeded((p) => ({ ...p, spread: true }));
    } else {
      setSelectedTotalIdx(idx);
      setSelectedTotalKey(lineChips[idx]?.key ?? null);
      setDefaultsSeeded((p) => ({ ...p, total: true }));
    }
  }, [marketType, lineChips, defaultsSeeded]);

  const rawSelectedLineIdx =
    marketType === 'spread' ? selectedSpreadIdx : selectedTotalIdx;
  const selectedLineKey =
    marketType === 'spread' ? selectedSpreadKey : selectedTotalKey;
  // Clamp the rendered selection to a valid index in case the chip
  // list shrank below the persisted index (mid-session re-derivation).
  const selectedLineIdx =
    lineChips.length === 0
      ? 0
      : lt2Access
        ? resolveLineIndexByKey(
            lineChips,
            selectedLineKey,
            (chip) => chip.key,
            pickDefaultChipIdx(lineChips),
          )
        : Math.min(rawSelectedLineIdx, lineChips.length - 1);

  const handleSelectLineIdx = (idx: number) => {
    if (marketType === 'spread') {
      setSelectedSpreadIdx(idx);
      setSelectedSpreadKey(lineChips[idx]?.key ?? null);
    } else if (marketType === 'total') {
      setSelectedTotalIdx(idx);
      setSelectedTotalKey(lineChips[idx]?.key ?? null);
    }
  };

  // Tickers the chart + trades table filter to. Always returns an
  // object in spread/total mode so the consumers stay in strict line-
  // filtering mode (returning null would fall back to moneyline
  // projection, which leaks Poly spread trades via the team-code
  // regex on Poly slugs). Null only for moneyline.
  const selectedLineTickers = useMemo(() => {
    if (marketType !== 'spread' && marketType !== 'total') return null;
    const chip = lineChips[selectedLineIdx];
    if (!chip) return { kalshi: null, polymarket: null };
    return chip.tickers;
  }, [marketType, lineChips, selectedLineIdx]);

  // Reset the manual pick on URL change. When the new URL carries a
  // `?team=` we honor it; otherwise we fall back to the auto-default.
  useEffect(() => {
    setUserPickedTeam(initialUrlTeam);
  }, [oddsKey, initialUrlTeam]);

  // SSE odds stream — always opened (regardless of viewMode) so the
  // analytics view can still consume Unabated's authoritative gameState
  // to confirm the game is final. Hydra 404s on pruned games; the hook
  // surfaces that as `status: 'error'` and the analytics view doesn't
  // depend on the stream for its rendering. The 256-listener cap easily
  // accommodates one connection per open GameDetail tab.
  const odds = useOddsStream(oddsKey);

  // (Removed Jul-22) The Unabated statusId auto-flip lived here — it moved
  // finished games onto the Archived/Analytics view. With that view disabled
  // there is nothing to flip to, and final games stay on Live. Restore from
  // git history (`isConfirmedFinal` + the scoreless-final soccer caveat)
  // if analytics ever ships.

  // Event-start epoch ms — used by GameScoreHeader's pre-game fallback when
  // no gameState event has arrived yet. Sourced via the canonical-key
  // resolver so spread/total/NO leg tickers all answer with the same value.
  const getEventStartTimeMsByTicker = useMarketStore((s) => s.getEventStartTimeMsByTicker);
  const scheduledStartMs = useMemo(() => {
    void version;
    const anyTicker =
      game?.kalshi?.away.marketTicker ??
      game?.kalshi?.home.marketTicker ??
      game?.polymarket?.away.marketTicker ??
      game?.polymarket?.home.marketTicker ??
      null;
    if (!anyTicker) return null;
    return getEventStartTimeMsByTicker(anyTicker);
  }, [game, getEventStartTimeMsByTicker, version]);

  // D-020 one-click on GameDetail: the arm control mounts in the page header
  // (gated on both capabilities) and auto-disarms whenever the identity or
  // page context changes — same discipline as ValueDashboard. Period/bet-type
  // tab flips inside GameLinesSection deliberately do NOT disarm: every LT2
  // click resolves a concrete ticker from the clicked line's own book at
  // click time, so context flips can't retarget an order.
  const oneClickAccess = useOneClickAccess();
  const showOneClickControl = oneClickAccess && lt2Access;
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const disarmOneClick = useOneClickStore((s) => s.disarm);
  const authUserId = useAuthStore((s) => s.user?.id ?? null);
  const authOrgId = useAuthStore((s) => s.user?.orgId ?? null);
  useEffect(() => {
    if (!showOneClickControl) disarmOneClick();
    return () => disarmOneClick();
  }, [showOneClickControl, disarmOneClick, authUserId, authOrgId, date, teamA, teamB, viewMode]);

  // Trade-ticket controller state. Pill clicks in MoneylineSection /
  // SpreadTotalSection swap this; the GameDetailPanel order ticket re-seeds
  // price + side on each ctx change via its own effect. Initial seed lands
  // when game first resolves so the panel isn't permanently empty.
  const [tradeCtx, setTradeCtx] = useState<TradeTicketContext | null>(null);
  // Soccer 3-way chip strip (Away / Home / Draw with prices) for the panel
  // header — built alongside each per-outcome tradeCtx. Null on binary games.
  const [multiBetCtx, setMultiBetCtx] = useState<BetLineContext | null>(null);

  // Map a soccer row/seed target to its per-outcome synth + chip strip. The
  // synth's |multi| key flips ticket labels to outcome-style ("CRY does not
  // win" / "Not Draw") — a NO on a 3-way leg is NOT the opponent winning.
  const multiOutcomeCtxFor = useCallback(
    (role: 'away' | 'home' | 'draw'): { game: GameRow; betCtx: BetLineContext } | null => {
      if (!multiGame) return null;
      const outcome =
        role === 'draw' ? multiGame.draw : role === 'away' ? multiGame.away : multiGame.home;
      if (!outcome) return null;
      return {
        game: synthesizeFromMultiOutcome(multiGame.event, outcome),
        betCtx: buildMultiOutcomeBetContext(multiGame.event, outcome),
      };
    },
    [multiGame],
  );

  useEffect(() => {
    if (tradeCtx) return;
    if (!game) return;

    // Knockout "To Advance" is the headline — seed it as a plain binary (no
    // multi remap, no chip strip). A focusTicker that targets the advance books
    // pins the right side; one that targets the 1X2 / a period market falls
    // through to the existing blocks below (which own those tickers).
    if (advanceGame) {
      const advBooks = [
        advanceGame.kalshi?.away.marketTicker,
        advanceGame.kalshi?.home.marketTicker,
        advanceGame.polymarket?.away.marketTicker,
        advanceGame.polymarket?.home.marketTicker,
      ].filter((t): t is string => !!t);
      const focusIsAdvance = !!focusTicker && advBooks.includes(focusTicker);
      if (!focusTicker || focusIsAdvance) {
        let teamSide: TeamSide =
          initialUrlTeam && initialUrlTeam === advanceGame.homeCode ? 'home' : 'away';
        let venue: Venue = 'kalshi';
        if (focusIsAdvance) {
          const cands: Array<[TeamSide, Venue, string | undefined]> = [
            ['away', 'kalshi', advanceGame.kalshi?.away.marketTicker],
            ['home', 'kalshi', advanceGame.kalshi?.home.marketTicker],
            ['away', 'polymarket', advanceGame.polymarket?.away.marketTicker],
            ['home', 'polymarket', advanceGame.polymarket?.home.marketTicker],
          ];
          const hit = cands.find(([, , t]) => t != null && t === focusTicker);
          if (hit) {
            teamSide = hit[0];
            venue = hit[1];
          }
        }
        setTradeCtx({ game: advanceGame, teamSide, venue, initialOrderSide: focusSide, postOnly: false });
        setMultiBetCtx(null);
        return;
      }
    }

    // Soccer: seed with the per-outcome synth, not the pair. focusTicker
    // (Positions deep-link) resolves across all three outcomes' books —
    // including the Draw, which the binary matching below can't reach.
    if (multiGame) {
      let role: 'away' | 'home' | 'draw' =
        initialUrlTeam && initialUrlTeam === game.homeCode ? 'home' : 'away';
      let venue: Venue = 'kalshi';
      if (focusTicker) {
        const candidates: Array<['away' | 'home' | 'draw', Venue, string | undefined]> = [
          ['away', 'kalshi', multiGame.away?.kalshi?.marketTicker],
          ['home', 'kalshi', multiGame.home?.kalshi?.marketTicker],
          ['draw', 'kalshi', multiGame.draw?.kalshi?.marketTicker],
          ['away', 'polymarket', multiGame.away?.polymarket?.marketTicker],
          ['home', 'polymarket', multiGame.home?.polymarket?.marketTicker],
          ['draw', 'polymarket', multiGame.draw?.polymarket?.marketTicker],
        ];
        const hit = candidates.find(([, , t]) => t != null && t === focusTicker);
        if (hit) {
          role = hit[0];
          venue = hit[1];
        }
      }
      const mapped = multiOutcomeCtxFor(role);
      if (mapped) {
        setTradeCtx({
          game: mapped.game,
          teamSide: 'away',
          venue,
          initialOrderSide: focusSide,
          postOnly: false,
        });
        setMultiBetCtx(mapped.betCtx);
        return;
      }
    }

    // If the URL carries a focusTicker (Positions page deep-link), seed
    // the trade ticket with the matching market + the requested side.
    // Walk the game's 4 moneyline books first; spread/total ladders
    // would need a different path via gameEvent — but the deep-link is
    // usable as-is for moneyline lines, and spread/total still gets
    // its line pre-selected via SpreadTotalSection's focusTicker
    // handling below (which seeds the ticket on render via pill-click).
    if (focusTicker) {
      const matches = (book: { marketTicker?: string } | undefined) =>
        !!book?.marketTicker && book.marketTicker === focusTicker;
      if (matches(game.kalshi?.away)) {
        setTradeCtx({ game, teamSide: 'away', venue: 'kalshi', initialOrderSide: focusSide, postOnly: false });
        return;
      }
      if (matches(game.kalshi?.home)) {
        setTradeCtx({ game, teamSide: 'home', venue: 'kalshi', initialOrderSide: focusSide, postOnly: false });
        return;
      }
      if (matches(game.polymarket?.away)) {
        setTradeCtx({ game, teamSide: 'away', venue: 'polymarket', initialOrderSide: focusSide, postOnly: false });
        return;
      }
      if (matches(game.polymarket?.home)) {
        setTradeCtx({ game, teamSide: 'home', venue: 'polymarket', initialOrderSide: focusSide, postOnly: false });
        return;
      }
      // No moneyline match — fall through to the default seed.
      // Spread/total focus is handled inside SpreadTotalSection, which
      // calls onPillClick the moment its initialFocusTicker matches
      // a line and overrides this seed.

      // NBA/WNBA period tickers (1H / quarters), soccer Game Lines tickers
      // (period spread/total/moneyline + BTTS at any scope), MLB First 5
      // Innings (F5 moneyline/spread/total), and NFL halves (1H/2H
      // moneyline/spread/total) are owned by GameLinesSection's own
      // deep-link focus — it switches its bet-type + period tabs and seeds the
      // ticket. Skip the full-game default seed so it doesn't flash/clobber.
      // Full-game soccer spread/total (no period, not BTTS) is NOT skipped here —
      // the top SpreadTotalSection owns it.
      if (/^KXW?NBA(1H|2H|[1-4]Q)(SPREAD|TOTAL|WINNER)/i.test(focusTicker)) return;
      if (/^KX(?:WC|UECL|INTLFRIENDLY)(?:(?:1H|2H)(?:SPREAD|TOTAL|BTTS)?|BTTS)-/i.test(focusTicker)) return;
      if (/^KXMLBF5(?:SPREAD|TOTAL)?-/i.test(focusTicker)) return;
      if (/^KXNFL(?:1H|2H)(?:SPREAD|TOTAL)?-/i.test(focusTicker)) return;
      // CFB (cfb-game-props): every Kalshi CFB series other than the three
      // full-game families (GAME/SPREAD/TOTAL) — periods (1H/2H/1Q–4Q winner,
      // spread, total, quarter BTTS) and the wire-classified game-event props
      // (1HFT, OT, 2PT, first-TD team, team stats, TOTALTD/FG, DSTTD) — is
      // owned by GameLinesSection's period chips / Events tab.
      if (/^KXNCAAF(?!(?:GAME|SPREAD|TOTAL)-)[A-Z0-9]+-/i.test(focusTicker)) return;
      // MLB player props + event markets (mlb-props-events) — owned by
      // GameLinesSection's Player Props / Events tabs. HRR before HR in the
      // alternation (alternation is ordered; HR would otherwise eat HRR).
      if (/^KXMLB(?:HIT|HRR|HR|RBI|TB|SB|KS|OUTS|TEAMTOTAL|RFI|EXTRAS)-/i.test(focusTicker)) return;
      // Poly side of the same tabs (poly-props-events): PM `-nrfi` /
      // `-extra-innings` / `-{hr|k}-{player}-{XptY}` deep links are also
      // owned by GameLinesSection — same skip, or the full-game default
      // seed clobbers its ticket (DNL-G4).
      if (classifyPolyExtendedTicker(focusTicker)) return;
    }

    const teamSide: TeamSide =
      initialUrlTeam && initialUrlTeam === game.homeCode ? 'home' : 'away';
    setTradeCtx({
      game,
      teamSide,
      venue: 'kalshi',
      initialOrderSide: focusSide,
      postOnly: false,
    });
  }, [game, advanceGame, multiGame, multiOutcomeCtxFor, initialUrlTeam, focusTicker, focusSide, tradeCtx]);
  // Always re-seed when the game identity changes so a tab navigation
  // doesn't carry the previous game's context.
  useEffect(() => {
    setTradeCtx(null);
    setMultiBetCtx(null);
    setActiveSpreadTotal(null);
    setGameLinesActive(null);
    setGameLinesLine(null);
  }, [oddsKey]);

  // Active spread/total bundle from SpreadTotalSection. Drives the
  // right-side panel's alternate (chip-strip) header + lets the user
  // toggle lines from inside the panel without bouncing back to the
  // section. Null when the user hasn't engaged the spread/total card
  // yet (and on moneyline pill click, which actively clears it).
  const [activeSpreadTotal, setActiveSpreadTotal] = useState<ActiveSpreadTotal | null>(null);
  // Owned at the parent so a moneyline pill click can deterministically
  // tell SpreadTotalSection to "stop being active". Without this, the
  // section's emit useEffect re-fires whenever its `event` prop's
  // reference churns (gameEvent re-derives on every market frame via
  // marketStore.version) and clobbers any null the parent just set —
  // panel ends up stuck on the prior spread/total bet.
  const [activeKind, setActiveKind] = useState<'spread' | 'total' | null>(null);
  // Same bundle, but emitted by the bottom Game Lines table (period
  // spread/total). Kept separate from activeSpreadTotal so the two tables
  // don't clobber each other's panel; the panel prefers this when set.
  const [gameLinesActive, setGameLinesActive] = useState<ActiveSpreadTotal | null>(null);
  // Parent-owned selection for the Game Lines table (mirrors activeKind) so the
  // parent can release it when the middle table / a moneyline takes over —
  // stopping Game Lines' emit from re-clobbering the panel.
  const [gameLinesLine, setGameLinesLine] = useState<{
    kind: 'spread' | 'total';
    key: string;
  } | null>(null);
  const [panelReleaseKey, setPanelReleaseKey] = useState(0);

  // Stable identity is load-bearing: SpreadTotalSection's emit effect lists
  // this callback in its deps and emits a freshly-allocated bundle. An inline
  // arrow here makes every parent render re-fire that effect, whose setState
  // re-renders the parent — an unbounded loop that saturates the main thread
  // the moment a spread/total line is focused (the "all clicks dead until
  // refresh" freeze traders hit Jun 8).
  const handleSpreadTotalPillClick = useCallback((ctx: TradeTicketContext) => {
    setGameLinesActive(null);
    setGameLinesLine(null);
    setPanelReleaseKey((n) => n + 1);
    setTradeCtx(ctx);
  }, [setGameLinesActive, setGameLinesLine, setPanelReleaseKey, setTradeCtx]);
  const handleSpreadTotalActiveLineChange = useCallback((v: ActiveSpreadTotal | null) => {
    // Middle (full-game) table taking over the panel → release the
    // bottom Game Lines selection so its emit stops re-clobbering.
    setActiveSpreadTotal(v);
    if (v) {
      setGameLinesLine(null);
      setGameLinesActive(null);
    }
  }, []);

  // Positions popup toggle — same UX as the value dashboard, just
  // anchored to the GameDetail page's right column. PositionsWidget
  // renders the small stat card above the order panel; clicking it
  // opens the floating PositionSidebar overlay.
  const [showPositionSidebar, setShowPositionSidebar] = useState(false);

  // Live game ref for the panel. tradeCtx.game is the snapshot at click
  // time; we resolve to fresh data on every render so K/P prices tick.
  // version dep ensures the memo recomputes on every marketStore frame.
  // When a spread/total context is active, prefer its synth — it's
  // already re-built in SpreadTotalSection from live market data on
  // every idx change, so it stays in lockstep with the chip strip
  // (including chip clicks from inside the panel itself).
  const livePanelGame = useMemo(() => {
    if (gameLinesActive) return gameLinesActive.game;
    if (activeSpreadTotal) return activeSpreadTotal.game;
    if (!tradeCtx) return null;
    void version;
    // Knockout "To Advance" headline — re-derived each frame (advanceGame is on
    // the version dep), so returning it keeps the panel's K/P books ticking live.
    if (advanceGame && tradeCtx.game.key === advanceGame.key) return advanceGame;
    if (game && tradeCtx.game.key === game.key) return game;
    // Soccer per-outcome synths re-derive from the live event each frame so
    // the panel's books tick; falls back to the click-time snapshot when the
    // event leaves the store (post-settle prune).
    const m = tradeCtx.game.key.indexOf('|multi|');
    if (m >= 0) {
      if (multiGame) {
        const code = tradeCtx.game.key.slice(m + '|multi|'.length);
        const outcome = multiGame.event.outcomes.find((o) => o.code === code);
        if (outcome) return synthesizeFromMultiOutcome(multiGame.event, outcome);
      }
      return tradeCtx.game;
    }
    // CFB Game Props enum/binary rows (cfb-game-props, DNL-001): re-resolve by
    // the row's real ticker from the CURRENT selector result so the panel's
    // book ticks. If the base game or exact ticker leaves the feed, fail closed
    // instead of reviving the click-time snapshot.
    const liveCfbEvent = resolveLiveCfbEventPanelGame(
      game,
      tradeCtx.game.key,
      (gameKey) => useMarketStore.getState().getCfbEventMarkets(gameKey),
    );
    if (liveCfbEvent !== undefined) return liveCfbEvent;
    const synth = canonicalKey
      ? findLiveSynthGameRow(tradeCtx.game.key, urlParts)
      : null;
    return synth ?? tradeCtx.game;
  }, [gameLinesActive, activeSpreadTotal, tradeCtx, game, advanceGame, multiGame, urlParts, canonicalKey, version]);

  // Pure derivation: manual pick > auto-favored team > alpha-first team.
  // No useEffect, no refs — eliminates the timing race that caused the
  // chart to render blank during pagination's rapid trade-state updates.
  const selectedTeam = useMemo(() => {
    if (userPickedTeam && bothTeams.includes(userPickedTeam)) {
      return userPickedTeam;
    }
    const favored = pickFavoredTeam(trades, bothTeams);
    return favored ?? bothTeams[0];
  }, [userPickedTeam, trades, bothTeams]);

  if (!canonicalKey) {
    return (
      <ErrorShell
        title="Bad game URL"
        message={`Couldn't parse a game from /${date ?? '?'}/${teamA ?? '?'}/${teamB ?? '?'}.`}
        onBack={handleBack}
      />
    );
  }

  // (Removed Jul-22) A `!hasAnalytics()` early return used to dark the whole
  // page when VITE_HYDRA_ANALYTICS_URL was unset. Live needs no analytics
  // endpoint, so gating it on one turned a missing env var into a dead game
  // detail page. Restore alongside the Analytics view if it ships.

  const heading = game
    ? `${game.awayCode} @ ${game.homeCode}`
    : `${bothTeams[0]} vs ${bothTeams[1]}`;
  const sportLabel = game?.sport ? game.sport.toUpperCase() : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 overflow-auto p-4 gap-4">
      <div className="flex flex-1 min-w-0 flex-col gap-3">
        {/* flex-wrap + a shrink-0 control cluster: under width pressure the
            armed controls (arm toggle, size, wrapped ack, target readout)
            move to the next line AS A UNIT instead of squeezing the title or
            each other — the taste gate's no-sibling-compression rule. */}
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            onClick={handleBack}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/40 -ml-1 rounded p-1"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          {/* min-w floor: without it flex-1 (basis-0) hands the whole row to
              the shrink-0 controls first and the title collapses to an
              ellipsis before the wrap ever fires — the identity must stay
              readable, so the cluster wraps instead. */}
          <div className="flex-1 min-w-[220px]">
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
              {sportLabel ? `${sportLabel} · ` : ''}
              {date}
            </div>
            <h1 className="text-foreground truncate text-xl font-semibold">
              {heading}
            </h1>
            <div className="text-muted-foreground/70 mt-0.5 font-mono text-[10px]">
              {oddsKey}
            </div>
          </div>
          <OrderbooksPausedBadge />
          {/* D-020: armed one-click for the derivative sections below. The
              control itself hides without oneClickAccess; the lt2 gate keeps
              GameDetail V1-identical until the trader is flagged in. */}
          {showOneClickControl && viewMode === 'live' && (
            <div className="flex shrink-0 items-center gap-2">
              <OneClickArmControl />
              <AttackArmControl />
              <Lt2TargetReadout />
              {lt2HotkeysEnabled && <Lt2HotkeyListener />}
            </div>
          )}
          <ViewToggle viewMode={viewMode} onChange={setViewMode} />
          <div className="text-muted-foreground text-[10px]">
            {viewMode === 'archived' && status === 'loading' && (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {total > 0
                  ? `loading ${loaded.toLocaleString()} / ${total.toLocaleString()}`
                  : 'loading trades'}
              </span>
            )}
            {viewMode === 'archived' && status === 'error' && (
              <span className="text-amber-500">
                trades: {error?.message ?? 'error'}
              </span>
            )}
            {viewMode === 'archived' && status === 'ok' && (
              <span>{loaded.toLocaleString()} trades</span>
            )}
            {viewMode === 'live' && odds.status !== 'open' && (
              <span className="text-amber-500/80 uppercase tracking-wider">
                odds: {odds.status}
              </span>
            )}
          </div>
        </header>

        {viewMode === 'live' ? (
          <>
            {/* Score header stays the first card ("this game right now");
                the consensus chart lives at the BOTTOM of the column per
                trader feedback (Prof, Jun 6: tables on top, graph at the
                bottom) — see the chart card after GameLinesSection.
                Re-landed Jun 11 after the Jun 10 rollback. */}
            {/* shrink-0: keep the status card at full height when the bet
                tables below grow on expand. Without it the card's
                overflow-hidden makes its flex min-height 0, so flexbox squeezes
                it away (clipping bottom-up). The column scrolls instead, so the
                trader can always scroll back up to the game status. */}
            <div className="bg-card border-border shrink-0 overflow-hidden rounded-lg border">
              <GameScoreHeader
                game={odds.snapshot?.game ?? null}
                gameState={odds.latestGameState}
                latestConsensus={
                  odds.latestConsensus.find(isFullGameResult) ?? null
                }
                fallbackTeams={bothTeams}
                scheduledStartMs={scheduledStartMs}
              />
            </div>
            {moneylineGame && (
              <MoneylineSection
                game={moneylineGame}
                drawGame={moneylineDrawGame}
                advanceConsensus={advanceGame != null}
                focusTicker={focusTicker}
                liveGameState={odds.latestGameState}
                onPillClick={(ctx) => {
                  // ── HELD FOR RELEASE JUDGMENT (order-path change) ──────
                  // GameDetail's upper moneyline joins the SAME armed
                  // one-click contract as the dashboard moneyline: clicks
                  // and this section's A/D hotkeys now agree, instead of a
                  // priced click while armed silently disarming (the old V1
                  // boundary that read as "one-click works below but dies
                  // above"). Same adapter, same guard, same 'one-click'
                  // grant, same audited lane. This is a DISTINCT behavior
                  // change from the #647-compat UX fixes around it and must
                  // be released deliberately — reverting is deleting this
                  // one early-return.
                  if (tryOneClickFromMoneylineCtx({ ctx, scope: LT2_GD_ML_CARD_SCOPE })) {
                    return;
                  }
                  // Non-eligible clicks (Polymarket, unpriced) keep the V1
                  // boundary: leaving the hot path disarms, and the disarm
                  // also clears any hotkey focus via the listener's
                  // subscription.
                  if (ctx.prefillPrice != null && useOneClickStore.getState().armed) {
                    disarmOneClick();
                  }
                  // Moneyline click → reset spread/total panel mode.
                  // Telling SpreadTotalSection (via activeKind=null)
                  // is the durable signal — its emit effect honors
                  // this on every render, so the panel can't get
                  // re-stuck on the prior bet when gameEvent's ref
                  // churns on the next market frame.
                  setActiveKind(null);
                  setActiveSpreadTotal(null);
                  setGameLinesActive(null);
                  setGameLinesLine(null);
                  setPanelReleaseKey((n) => n + 1);
                  // Knockout "To Advance" headline: a plain binary market, so the
                  // 1X2 multi-outcome remap below must NOT fire (it would re-point
                  // the bet at the 3-way away/home outcome). Seed it as-is.
                  if (advanceGame && ctx.game.key === advanceGame.key) {
                    setMultiBetCtx(null);
                    setTradeCtx(ctx);
                    return;
                  }
                  // Soccer: the section hands back the pair (or the Draw
                  // synth) — remap to the per-outcome synth so the ticket
                  // labels read outcome-style and the chip strip seeds.
                  if (multiGame) {
                    const role: 'away' | 'home' | 'draw' =
                      drawGame && ctx.game.key === drawGame.key ? 'draw' : ctx.teamSide;
                    const mapped = multiOutcomeCtxFor(role);
                    if (mapped) {
                      setTradeCtx({ ...ctx, game: mapped.game, teamSide: 'away' });
                      setMultiBetCtx(mapped.betCtx);
                      return;
                    }
                  }
                  setTradeCtx(ctx);
                }}
              />
            )}
            {game && gameEvent && (
              <SpreadTotalSection
                game={game}
                event={gameEvent}
                focusTicker={focusTicker}
                focusSide={focusSide}
                activeKind={activeKind}
                onActiveKindChange={setActiveKind}
                onPillClick={handleSpreadTotalPillClick}
                onActiveLineChange={handleSpreadTotalActiveLineChange}
              />
            )}
            {/* "Game Lines" — separate, additive period table (NBA 1H/quarters,
                soccer 1H/2H + BTTS, MLB First 5 Innings). Renders null for
                sports it doesn't support and for games with no period markets.
                A period pill click re-seeds the trade ticket like the full-game
                sections. */}
            {game && (
              <GameLinesSection
                key={`game-lines-${panelReleaseKey}`}
                game={game}
                focusTicker={focusTicker}
                focusSide={focusSide}
                onPillClick={(ctx) => {
                  // Stop the middle table from clobbering the panel; the Game
                  // Lines bet-line context (if any) arrives via onActiveLineChange.
                  setActiveKind(null);
                  setActiveSpreadTotal(null);
                  setTradeCtx(ctx);
                }}
                panelLine={gameLinesLine}
                onPanelLineChange={setGameLinesLine}
                onActiveLineChange={setGameLinesActive}
              />
            )}
            {/* Consensus chart — bottom of the column per trader feedback
                (tables first, graph last). shrink-0 is REQUIRED: same
                overflow-hidden + fixed-height clip trap as the score card
                above (chart body is a fixed 420px). */}
            <div className="bg-card border-border shrink-0 overflow-hidden rounded-lg border">
              <ConsensusChart
                consensusPoints={odds.consensusPoints}
                latestConsensus={
                  odds.latestConsensus.find(isFullGameResult) ?? null
                }
                trend={odds.consensusTrends.get('1|result') ?? null}
                gameState={odds.latestGameState}
                awayTeam={odds.snapshot?.game.awayTeam ?? bothTeams[0]}
                homeTeam={odds.snapshot?.game.homeTeam ?? bothTeams[1]}
                sourcesHref={
                  sport
                    ? `/game/${sport}/${date}/${teamA}/${teamB}${gameNo ? `/${gameNo}` : ''}/sources`
                    : `/game/${date}/${teamA}/${teamB}/sources`
                }
              />
            </div>
          </>
        ) : (
          <>
            {/* Analytics-mode disclaimer. Terse on purpose — this view
                is a stop-gap for CSV exports + raw trade inspection
                until proper analytics ship. */}
            <div className="border-amber-500/40 bg-amber-500/10 text-amber-200 light:text-amber-700 flex-shrink-0 rounded-lg border px-3 py-2 text-xs leading-snug">
              This page is under construction — for now, export a .csv of
              all the Polymarket and Kalshi data for this event in the
              table below.
            </div>
            <section className="bg-card border-border flex-shrink-0 rounded-lg border">
              <Toolbar
                marketType={marketType}
                onSelectMarketType={setMarketType}
                bothTeams={bothTeams}
                selectedTeam={selectedTeam}
                enabledVenues={enabledVenues}
                lineChips={lineChips}
                selectedLineIdx={selectedLineIdx}
                onSelectLineIdx={handleSelectLineIdx}
                onSelectTeam={setUserPickedTeam}
                onToggleVenue={(v) => {
                  setEnabledVenues((prev) => {
                    const next = new Set(prev);
                    if (next.has(v)) {
                      // Don't allow turning off both venues — at least one stays on
                      if (next.size > 1) next.delete(v);
                    } else {
                      next.add(v);
                    }
                    return next;
                  });
                }}
              />
                <div className="p-3">
                  <PriceChart
                    mode={chartMode}
                    trades={trades}
                    selectedTeam={selectedTeam}
                    bothTeams={bothTeams}
                    enabledVenues={enabledVenues}
                    game={game}
                    lineTickers={selectedLineTickers}
                  />
                </div>
            </section>

            <section className="bg-card border-border rounded-lg border">
              <div className="border-border/60 flex items-center justify-between border-b px-3 py-2">
                <h2 className="text-foreground text-sm font-semibold">Trades</h2>
                <div className="text-muted-foreground text-[10px]">
                  {total > 0
                    ? `${trades.length.toLocaleString()} of ${total.toLocaleString()} loaded`
                    : ''}
                </div>
              </div>
              <TradesTable
                trades={trades}
                game={game}
                selectedTeam={selectedTeam}
                bothTeams={bothTeams}
                dataMode={dataMode}
                onDataModeChange={setDataMode}
                lineTickers={selectedLineTickers}
                lineLabel={lineChips[selectedLineIdx]?.label ?? null}
              />
            </section>
          </>
        )}
      </div>

      {viewMode === 'live' && tradeCtx && livePanelGame && (
        <aside className="sticky top-6 self-start w-96 shrink-0 h-[calc(100vh-3rem)] flex flex-col gap-3">
          {/* Positions snapshot card above the order panel — same
              PositionsWidget the value dashboard mounts in its toolbar
              row. Click expands the floating PositionSidebar overlay
              just like on /value. Visually distinct from the trade
              panel below it: full rounded border + a gap between the
              two cards so "my positions / stats" reads as a separate
              concern from "this game's trade form." */}
          <div className="bg-card border-border shrink-0 overflow-hidden rounded-lg border">
            <PositionsWidget
              expanded={showPositionSidebar}
              onToggle={() => setShowPositionSidebar((v) => !v)}
            />
          </div>

          {/* Single panel = order entry + dual-venue orderbooks. Replaces
              the prior split of TradeTicketPanel + OrderbookPanel; this
              is the value-dashboard's GameDetailPanel reused. Pill clicks
              from MoneylineSection / SpreadTotalSection flow into
              tradeCtx, which we map onto the panel's tradeIntent prop so
              the order ticket seeds correctly.

              tradeCtx.game is a snapshot captured at click time. To keep
              K/P prices ticking live we resolve the panel's game prop
              against the current marketStore on every render:
                • Moneyline tradeCtx → page-level live `game` (matches
                  via canonical key).
                • Spread/total tradeCtx → re-synthesize from the live
                  GameEventRow + the matching line ticker.
                • CFB Game Props tradeCtx → re-resolve the exact live
                  ticker; hide the panel if that market leaves the feed. */}
          {/* Match the PositionsWidget card's full rounded border so
              the two cards in the right column read as a matched pair.
              overflow-hidden clips the panel's children (orderbooks,
              borders) against the outer radius cleanly. min-h-0 +
              flex-1 lets this wrapper fill the aside's remaining
              vertical space so the panel inside can keep its h-full
              behavior for internal scroll. */}
          <div className="bg-card border-border min-h-0 flex-1 overflow-hidden rounded-lg border">
          <GameDetailPanel
            key={tradeCtx.game.key}
            game={livePanelGame}
            marketSide={tradeCtx.initialOrderSide ?? 'yes'}
            tradeIntent={tradeCtxToTradeIntent(tradeCtx)}
            betLineContext={gameLinesActive?.context ?? activeSpreadTotal?.context ?? multiBetCtx}
            unabated={(() => {
              const c = odds.latestConsensus.find(isFullGameResult);
              if (!c) return null;
              // Unabated's awayTeam can land in g1's homeCode slot when
              // marketStore's away/home convention disagrees with the
              // venue's real away. When that happens, all three rows
              // (U, K, P) need to swap columns — the K and P books are
              // sourced from the same GameRow positions, so they're
              // mis-aligned the same way. Compute once, apply
              // uniformly via swapVenueColumns.
              const unabatedAwayCode = odds.snapshot?.game.awayTeam;
              const swapped =
                unabatedAwayCode != null &&
                unabatedAwayCode === livePanelGame.homeCode;
              return {
                awayCents: swapped ? c.homeFairProbCents : c.awayFairProbCents,
                homeCents: swapped ? c.awayFairProbCents : c.homeFairProbCents,
                // The order-entry U row displays the tenth-accurate value
                // when hydra sends it (integer cents remain the fallback +
                // the glow key).
                awayTenths: (swapped ? c.homeFairProbTenths : c.awayFairProbTenths) ?? null,
                homeTenths: (swapped ? c.awayFairProbTenths : c.homeFairProbTenths) ?? null,
                // Sub-cent trend + sharp anchor for the U row's glow
                // treatments; the panel re-applies `swapped` per side.
                trend: odds.consensusTrends.get('1|result') ?? null,
                swapped,
                sharpBookUsed: c.sharpBookUsed ?? null,
              };
            })()}
          />
          </div>
        </aside>
      )}

      {/* Positions overlay — fires off the PositionsWidget toggle above. */}
      {showPositionSidebar && (
        <PositionSidebar onClose={() => setShowPositionSidebar(false)} />
      )}
      </div>
    </div>
  );
}


function ViewToggle({
  viewMode,
  onChange,
}: {
  viewMode: 'live' | 'archived';
  onChange: (next: 'live' | 'archived') => void;
}) {
  return (
    <div className="border-border/60 inline-flex items-center gap-0.5 rounded-md border bg-muted/40 p-0.5">
      {(['live', 'archived'] as const).map((m) => {
        // Analytics is parked — the trade-bubble surface never got past
        // stop-gap, so the tab stays visible but inert (greyed, not
        // clickable) rather than disappearing. Flip `disabled` off to
        // bring it back.
        const disabled = m === 'archived';
        return (
          <button
            key={m}
            type="button"
            disabled={disabled}
            aria-disabled={disabled}
            title={disabled ? 'Analytics view is unavailable' : undefined}
            onClick={() => {
              if (!disabled) onChange(m);
            }}
            className={`rounded px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
              disabled
                ? 'text-muted-foreground/40 cursor-not-allowed'
                : viewMode === m
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {m === 'live' ? 'Live' : 'Analytics'}
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({
  marketType,
  onSelectMarketType,
  bothTeams,
  selectedTeam,
  enabledVenues,
  lineChips,
  selectedLineIdx,
  onSelectTeam,
  onSelectLineIdx,
  onToggleVenue,
}: {
  marketType: MarketTypeFilter;
  onSelectMarketType: (m: MarketTypeFilter) => void;
  bothTeams: readonly [string, string];
  selectedTeam: string;
  enabledVenues: ReadonlySet<Venue>;
  lineChips: { key: string; label: string; priceCents: number | null }[];
  selectedLineIdx: number;
  onSelectTeam: (t: string) => void;
  onSelectLineIdx: (idx: number) => void;
  onToggleVenue: (v: Venue) => void;
}) {
  const isLineMode = marketType === 'spread' || marketType === 'total';
  return (
    <>
      <div className="border-border/60 flex flex-wrap items-center gap-x-5 gap-y-2 border-b px-3 py-2">
        <ControlGroup label="Market">
          {MARKET_TYPES.map((m) => (
            <Chip
              key={m.id}
              active={marketType === m.id}
              onClick={() => onSelectMarketType(m.id)}
            >
              {m.label}
            </Chip>
          ))}
        </ControlGroup>

        {/* Team selector is moneyline-only. Spread/total are
            single-ticker-per-line markets where YES/NO encodes the
            outcome directly — no team-toggle equivalent. */}
        {!isLineMode && (
          <ControlGroup label="Team">
            {bothTeams.map((t) => (
              <Chip
                key={t}
                active={selectedTeam === t}
                onClick={() => onSelectTeam(t)}
              >
                {t}
              </Chip>
            ))}
          </ControlGroup>
        )}

        <ControlGroup label="Source">
          <Chip
            active={enabledVenues.has('kalshi')}
            onClick={() => onToggleVenue('kalshi')}
          >
            Kalshi
          </Chip>
          <Chip
            active={enabledVenues.has('polymarket')}
            onClick={() => onToggleVenue('polymarket')}
            activeClassName="bg-purple-500 text-white border-purple-500"
          >
            Polymarket
          </Chip>
        </ControlGroup>
      </div>

      {/* Second toolbar row — only present in spread/total mode. The
          line scroller has variable width and needs its own row so the
          chevron buttons + chip strip don't get squeezed inline next
          to Market/Source. Renders even when lineChips is empty so the
          user gets a clear status message instead of a silent missing
          toggle. */}
      {isLineMode && (
        <div className="border-border/60 flex flex-wrap items-center gap-x-5 gap-y-2 border-b px-3 py-2">
          <ControlGroup label="Line">
            {lineChips.length === 0 ? (
              <span className="text-muted-foreground/70 text-[11px]">
                No {marketType} trades found for this game yet
              </span>
            ) : (
              <MiniLineStrip
                chips={lineChips}
                selectedIdx={selectedLineIdx}
                onSelect={onSelectLineIdx}
              />
            )}
          </ControlGroup>
        </div>
      )}
    </>
  );
}

function ControlGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground text-[10px] uppercase tracking-wider">
        {label}
      </span>
      <div className="flex items-center gap-1">{children}</div>
    </div>
  );
}

function Chip({
  active,
  disabled,
  onClick,
  title,
  activeClassName,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  title?: string;
  /** Override active-state classes — used so the Polymarket source chip
   *  picks up the same purple as the chart bubbles. Defaults to the
   *  app's primary token. */
  activeClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      className={`rounded border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? activeClassName ?? 'bg-primary text-primary-foreground border-primary'
          : disabled
            ? 'border-border/30 text-muted-foreground/40 cursor-not-allowed'
            : 'border-border/50 text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

// Walk the trade set for the most recent YES trade per team and pick the
// team with the higher latest YES price — i.e. the team the market thinks
// is winning. Returns null when neither team has a parseable YES trade
// (in which case the existing default stays).
function pickFavoredTeam(
  trades: TradeRow[],
  bothTeams: readonly [string, string],
): string | null {
  const latest: Record<string, { ts: number; price: number }> = {
    [bothTeams[0]]: { ts: 0, price: -1 },
    [bothTeams[1]]: { ts: 0, price: -1 },
  };
  for (const t of trades) {
    if ((t.side ?? '').toLowerCase() !== 'yes') continue;
    const team = tickerNamedTeam(t.ticker, t.source, bothTeams);
    if (!team || !(team in latest)) continue;
    const ts = Date.parse(t.block_ts ?? t.ts);
    if (!Number.isFinite(ts)) continue;
    if (ts > latest[team].ts) {
      latest[team] = { ts, price: t.price };
    }
  }
  const a = latest[bothTeams[0]];
  const b = latest[bothTeams[1]];
  if (a.price < 0 && b.price < 0) return null;
  if (a.price < 0) return bothTeams[1];
  if (b.price < 0) return bothTeams[0];
  return a.price >= b.price ? bothTeams[0] : bothTeams[1];
}

function ErrorShell({
  title,
  message,
  onBack,
}: {
  title: string;
  message: string;
  onBack: () => void;
}) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="bg-card border-border max-w-md rounded-lg border p-5 text-center">
        <h1 className="text-foreground text-base font-semibold">{title}</h1>
        <p className="text-muted-foreground mt-2 text-xs">{message}</p>
        <button
          onClick={onBack}
          className="bg-primary text-primary-foreground hover:bg-primary/90 mt-4 rounded px-3 py-1.5 text-xs"
        >
          Back
        </button>
      </div>
    </div>
  );
}

export default GameDetail;
